<template>
  <div class="prompt-config-container">
    <h2 class="text-2xl font-bold mb-6 flex items-center gap-2">
      LLM Prompt Configuration
      <BaseInfoIcon :text="ERKLAERUNG.promptKonfiguration" label="LLM Prompt Configuration" />
    </h2>
    
    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    </div>

    <!-- Error State -->
    <div v-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
      <strong class="font-bold">Error:</strong>
      <span class="block sm:inline"> {{ error }}</span>
    </div>

    <!-- Success Message -->
    <div v-if="successMessage" class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
      <strong class="font-bold">Success:</strong>
      <span class="block sm:inline"> {{ successMessage }}</span>
    </div>

    <!-- Content -->
    <div v-if="!loading" class="space-y-6">
      <!-- LLM Model Selection -->
      <div class="bg-white border border-gray-200 rounded-lg p-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-lg font-semibold text-gray-900">LLM Model</h3>
          <span class="text-xs text-gray-500">Source: Ollama</span>
        </div>
        <p class="text-sm text-gray-600 mb-4">
          Choose a model to use for both LLM and RAG pipelines. This overrides the env default.
        </p>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Available Models</label>
            <select
              v-model="llmModel"
              @change="applySelectedModel"
              class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              :disabled="llmModelLoading"
            >
              <option value="">-- Select a model --</option>
              <option v-for="model in llmModels" :key="model" :value="model">
                {{ model }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Selected Model</label>
            <input
              v-model="llmModelInput"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="e.g. gemma3:4b"
              :disabled="llmModelLoading"
            />
          </div>
          <div>
            <button
              @click="saveLlmModel"
              :disabled="llmModelSaving || llmModelLoading || !llmModelInput"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {{ llmModelSaving ? 'Saving...' : 'Save Model' }}
            </button>
          </div>
        </div>

        <div class="mt-5 border-t border-gray-200 pt-4">
          <p class="text-sm font-medium text-gray-700 mb-2">Pull from Ollama Registry</p>
          <div class="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Search Registry</label>
              <input
                v-model="catalogQuery"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="e.g. gemma, llama"
                :disabled="catalogLoading || !!pullingModel"
              />
            </div>
            <div>
              <button
                @click="loadModelCatalog"
                :disabled="catalogLoading || !!pullingModel"
                class="w-full px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {{ catalogLoading ? 'Loading...' : 'Search' }}
              </button>
            </div>
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-2">Registry Models</label>
              <select
                v-model="selectedCatalogModel"
                class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                :disabled="catalogLoading || !!pullingModel"
              >
                <option value="">-- Select a registry model --</option>
                <option
                  v-for="item in catalogModels"
                  :key="item.name"
                  :value="item.name"
                >
                  {{ item.name }}{{ item.is_local ? ' (already pulled)' : '' }}
                </option>
              </select>
            </div>
          </div>
          <div class="mt-3 flex flex-col gap-2">
            <div v-if="pullingModel && pullJobStatus" class="text-sm">
              <span class="font-medium">Pull Status:</span>
              <span class="ml-2 px-2 py-1 rounded-md"
                :class="{
                  'bg-yellow-100 text-yellow-800': pullJobStatus === 'pending',
                  'bg-blue-100 text-blue-800': pullJobStatus === 'running',
                  'bg-green-100 text-green-800': pullJobStatus === 'completed'
                }">
                {{ pullJobStatus.toUpperCase() }}
              </span>
              <span v-if="pullJobStatus === 'running'" class="ml-2 text-gray-600">
                (This may take several minutes for large models)
              </span>
            </div>
            <div class="flex justify-end">
              <button
                @click="pullSelectedModel"
                :disabled="!selectedCatalogModel || !!pullingModel || catalogLoading"
                class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {{ pullingModel ? `Pulling ${pullingModel}...` : 'Pull Selected Model' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Description -->
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 class="font-semibold text-blue-900 mb-2">Evaluation and Recommendation Generation Prompt</h3>
        <p class="text-blue-800 text-sm">
          This template controls how the LLM generates evaluation and recommendation texts for students.
          The template uses placeholders that will be replaced with actual data:
        </p>
        <ul class="text-blue-800 text-sm mt-2 ml-4 list-disc">
          <li><code class="bg-blue-100 px-1 rounded">{user_name}</code> - Name of the participant</li>
          <li><code class="bg-blue-100 px-1 rounded">{user_role}</code> - Role of the participant (e.g., DEV, PO, SM)</li>
          <li><code class="bg-blue-100 px-1 rounded">{criterion_descriptions}</code> - Descriptions of all evaluation criteria</li>
          <li><code class="bg-blue-100 px-1 rounded">{comment_list}</code> - List of textual comments for each criterion (includes user and team comments with value changes, e.g., "+2" or "-1")</li>
          <li><code class="bg-blue-100 px-1 rounded">{user_criteria_table}</code> - <strong>NEW:</strong> Table with user criterion values and role-specific averages (Format: Criterion | Value | Average)</li>
          <li><code class="bg-blue-100 px-1 rounded">{team_criteria_table}</code> - <strong>NEW:</strong> Table with team criterion values without averages (Format: Criterion | Value)</li>
        </ul>
      </div>

      <!-- View Mode -->
      <div v-if="!isEditing">
        <div class="flex justify-between items-center mb-3">
          <h3 class="text-lg font-semibold">Current Prompt Template</h3>
          <button
            @click="startEdit"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Edit
          </button>
        </div>
        <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <pre class="whitespace-pre-wrap text-sm text-gray-800 font-mono">{{ promptTemplate || 'No template configured' }}</pre>
        </div>
      </div>

      <!-- Edit Mode -->
      <div v-else>
        <label for="promptTemplate" class="block text-sm font-medium text-gray-700 mb-2">
          Edit Prompt Template
        </label>
        <textarea
          id="promptTemplate"
          v-model="promptTemplate"
          rows="20"
          class="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter your prompt template here..."
        ></textarea>
        <p class="text-sm text-gray-500 mt-1">
          Available placeholders: {user_name}, {user_role}, {criterion_descriptions}, {comment_list}, {criteria_table}
        </p>

        <!-- Action Buttons -->
        <div class="flex gap-4 mt-4">
          <button
            @click="savePrompt"
            :disabled="saving || !promptTemplate"
            class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>

          <button
            @click="cancelEdit"
            :disabled="loading || saving"
            class="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Cancel Confirmation Modal -->
    <ConfirmModal
      :isOpen="showCancelModal"
      title="Cancel Changes"
      message="Are you sure you want to cancel? This will discard any unsaved changes."
      @confirm="confirmCancel"
      @cancel="showCancelModal = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { systemSettingsApi } from '@/api/systemSettings';
import ConfirmModal from '@/BaseComponents/ConfirmModal.vue';
import BaseInfoIcon from '@/BaseComponents/BaseInfoIcon.vue';
import { ERKLAERUNG } from '@/async-sessions/erklaerungen';

const loading = ref(false);
const saving = ref(false);
const error = ref(null);
const successMessage = ref(null);
const promptTemplate = ref('');
const settingExists = ref(false);
const showCancelModal = ref(false);
const isEditing = ref(false);
const llmModels = ref([]);
const llmModel = ref('');
const llmModelInput = ref('');
const llmModelLoading = ref(false);
const llmModelSaving = ref(false);
const catalogModels = ref([]);
const catalogLoading = ref(false);
const catalogQuery = ref('');
const selectedCatalogModel = ref('');
const pullingModel = ref('');
const pullJobId = ref(null);
const pullJobStatus = ref(null);
let pollInterval = null;

// Hardcoded description for evaluation and recommendation generation
const PROMPT_DESCRIPTION = 'LLM prompt template for generating student evaluation and recommendation texts';

const defaultPrompt = `Teilnehmer {user_name} (Rolle: {user_role}) hat an einer Kommunikationsschulung teilgenommen. 

Dabei werden Kriterien beobachtet und bewertet, die sich wie folgt umschreiben lassen:
{criterion_descriptions}

TATSÄCHLICHE BEWERTUNG - Von dieser Schulung liegen folgende Ergebnisse vor, die im folgenden aufgelistet sind:

- "Kommentarliste": Textuelle Bewertungen im Format "- Kriteriumname: Kommentartext"
  Jeder Kommentar ist einem Kriterium zugeordnet
- "USER Kriterienwertetabelle": Tabelle mit drei Spalten
  Spalte 1: Relevantes Bewertungskriterium
  Spalte 2: Der vom Teilnehmer in diesem Kriterium erreichte Wert
  Spalte 3: Der Durchschnittswert in diesem Kriterium über alle Teilnehmer mit Rolle {user_role}
- "TEAM Kriterienwertetabelle": Tabelle mit zwei Spalten
  Spalte 1: Relevantes Bewertungskriterium der Teams
  Spalte 2: Der von den Teams erreichte Wert (ohne Durchschnitt)

Kommentarliste:
{comment_list}

USER Kriterienwertetabelle (alle Kriterien):
Format: Kriteriumname | Wert | Durchschnitt (Rolle: {user_role})
{user_criteria_table}

TEAM Kriterienwertetabelle (nur Kriterien der Teams des Teilnehmers):
Format: Kriteriumname | Wert
{team_criteria_table}

(1) INDIVIDUELLE BEURTEILUNG (maximal 10 Sätze):

REGELN:
1. Verwende nur Kriterien aus der USER Wertetabelle (Wert != 0)
2. Reihenfolge: Erst positive Kriterien (über Durchschnitt), dann negative (unter Durchschnitt)
3. Ignoriere Kriterien im Durchschnitt (+/- 0,5) und negative Soft-Skills (Emotionale Intelligenz, Smalltalk, Teamgeist)
4. Baue normale Kommentare (ohne "TEAM:" oder "RESUMEE:") wörtlich ein
5. RESUMEE: Suche nach "RESUMEE:" in der Kommentarliste, entferne das Präfix und übernehme den Text EXAKT WÖRTLICH ans Ende (vor dem Abschlusssatz)
6. Abschlusssatz: "In allen anderen Kriterien sind Sie nicht aufgefallen."
7. Spreche mit 'Sie' an, schreibe im Präsens
8. Team-Werte werden hier NICHT berücksichtigt

(2) TEAM BEURTEILUNG (1-3 Sätze oder leer ""):

WICHTIG: Hier wird das TEAM als Ganzes beurteilt, NICHT der einzelne Teilnehmer!

REGELN:
- Suche nach "TEAM:" in der Kommentarliste und verwende diese Kommentare BEVORZUGT
- Sprich das Team in der 3. Person an ("Das Team...", "Sie als Team..."), NICHT den einzelnen Teilnehmer
- Verwende die TEAM Kriterienwertetabelle für die Beurteilung der Teamleistung
- Fasse die Team-Kommentare zu einer kompakten Beurteilung zusammen
- Falls keine TEAM-Kommentare oder TEAM-Tabelle leer: gib "" zurück

Abkürzungen: DEV = Entwickler, PO = Product Owner, SM = Scrum Master

(3) EMPFEHLUNG:

REGELN:
- Lobe starke Kriterien (deutlich über Durchschnitt)
- Gib Verbesserungsvorschläge für schwache Kriterien (unter/nahe Durchschnitt)
- Nutze die Kriterien-Beschreibungen für konkrete Tipps

JSON-Format:
{{
  "individual_evaluation": "...",
  "team_evaluation": "...",
  "recommendation": "..."
}}`;

const loadPrompt = async () => {
  loading.value = true;
  error.value = null;
  successMessage.value = null;

  try {
    const response = await systemSettingsApi.getPromptTemplate();
    promptTemplate.value = response.data.value || '';
    settingExists.value = true;
  } catch (err) {
    if (err.response?.status === 404) {
      // Setting doesn't exist yet, use default
      promptTemplate.value = defaultPrompt;
      settingExists.value = false;
    } else {
      error.value = err.response?.data?.detail || 'Failed to load prompt template';
    }
  } finally {
    loading.value = false;
  }
};

const loadLlmModels = async () => {
  llmModelLoading.value = true;
  error.value = null;

  try {
    const response = await systemSettingsApi.getLlmModels();
    llmModels.value = response.data.models || [];
    llmModel.value = response.data.configured_model || '';
    llmModelInput.value = llmModel.value;
  } catch (err) {
    error.value = err.response?.data?.detail || 'Failed to load LLM models';
  } finally {
    llmModelLoading.value = false;
  }
};

const applySelectedModel = () => {
  llmModelInput.value = llmModel.value;
};

const loadModelCatalog = async () => {
  catalogLoading.value = true;
  error.value = null;

  try {
    const response = await systemSettingsApi.getLlmModelCatalog(catalogQuery.value, 200);
    catalogModels.value = response.data.catalog_models || [];
  } catch (err) {
    error.value = err.response?.data?.detail || 'Failed to load model catalog';
  } finally {
    catalogLoading.value = false;
  }
};

const pullSelectedModel = async () => {
  const modelName = (selectedCatalogModel.value || '').trim();
  if (!modelName) {
    error.value = 'Please select a model to pull';
    return;
  }

  pullingModel.value = modelName;
  error.value = null;
  successMessage.value = null;

  try {
    // Initiate async pull job
    const response = await systemSettingsApi.pullLlmModel(modelName);
    pullJobId.value = response.data.job_id;
    pullJobStatus.value = response.data.status;

    // Start polling for job status
    startPullJobPolling();
  } catch (err) {
    error.value = err.response?.data?.detail || `Failed to initiate pull for ${modelName}`;
    pullingModel.value = '';
  }
};

const startPullJobPolling = () => {
  if (pollInterval) {
    clearInterval(pollInterval);
  }

  pollInterval = setInterval(async () => {
    if (!pullJobId.value) {
      clearInterval(pollInterval);
      return;
    }

    try {
      const response = await systemSettingsApi.getPullJobStatus(pullJobId.value);
      pullJobStatus.value = response.data.status;

      if (response.data.status === 'completed') {
        clearInterval(pollInterval);
        successMessage.value = `Model ${pullingModel.value} pulled successfully!`;
        llmModelInput.value = pullingModel.value;
        
        // Reload models and catalog
        await loadLlmModels();
        await loadModelCatalog();
        
        // Cleanup
        pullingModel.value = '';
        pullJobId.value = null;
        pullJobStatus.value = null;

        setTimeout(() => {
          successMessage.value = null;
        }, 3000);
      } else if (response.data.status === 'failed') {
        clearInterval(pollInterval);
        error.value = `Failed to pull model ${pullingModel.value}: ${response.data.error || 'Unknown error'}`;
        
        // Cleanup
        pullingModel.value = '';
        pullJobId.value = null;
        pullJobStatus.value = null;
      }
      // If status is 'pending' or 'running', keep polling
    } catch (err) {
      clearInterval(pollInterval);
      error.value = err.response?.data?.detail || 'Failed to check pull job status';
      pullingModel.value = '';
      pullJobId.value = null;
      pullJobStatus.value = null;
    }
  }, 2000); // Poll every 2 seconds
};

const saveLlmModel = async () => {
  const nextModel = (llmModelInput.value || '').trim();
  if (!nextModel) {
    error.value = 'Model name cannot be empty';
    return;
  }

  llmModelSaving.value = true;
  error.value = null;
  successMessage.value = null;

  try {
    await systemSettingsApi.updateLlmModel(nextModel);
    successMessage.value = 'LLM model updated successfully!';
    llmModel.value = nextModel;
    window.dispatchEvent(new CustomEvent('llm-model-updated', { detail: { model: nextModel } }));
    if (!llmModels.value.includes(nextModel)) {
      llmModels.value = [nextModel, ...llmModels.value];
    }
    setTimeout(() => {
      successMessage.value = null;
    }, 3000);
  } catch (err) {
    error.value = err.response?.data?.detail || 'Failed to save LLM model';
  } finally {
    llmModelSaving.value = false;
  }
};

const startEdit = () => {
  isEditing.value = true;
};

const savePrompt = async () => {
  if (!promptTemplate.value) {
    error.value = 'Prompt template cannot be empty';
    return;
  }

  saving.value = true;
  error.value = null;
  successMessage.value = null;

  try {
    if (settingExists.value) {
      await systemSettingsApi.updatePromptTemplate(promptTemplate.value, PROMPT_DESCRIPTION);
      successMessage.value = 'Prompt template updated successfully!';
    } else {
      await systemSettingsApi.createPromptTemplate(promptTemplate.value, PROMPT_DESCRIPTION);
      settingExists.value = true;
      successMessage.value = 'Prompt template created successfully!';
    }

    isEditing.value = false;

    // Clear success message after 3 seconds
    setTimeout(() => {
      successMessage.value = null;
    }, 3000);
  } catch (err) {
    error.value = err.response?.data?.detail || 'Failed to save prompt template';
  } finally {
    saving.value = false;
  }
};

const cancelEdit = () => {
  showCancelModal.value = true;
};

const confirmCancel = () => {
  showCancelModal.value = false;
  isEditing.value = false;
  loadPrompt();
};

onMounted(() => {
  loadPrompt();
  loadLlmModels();
  loadModelCatalog();
});

onUnmounted(() => {
  if (pollInterval) {
    clearInterval(pollInterval);
  }
});
</script>

<style scoped>
.prompt-config-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

code {
  font-family: 'Courier New', Courier, monospace;
}
</style>
